import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import {
  ArrowPathRoundedSquareIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  EyeDropperIcon,
  EyeIcon,
  ViewColumnsIcon,
  ViewfinderCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFile,
  faFileExcel,
  faFilePdf,
} from "@fortawesome/free-regular-svg-icons";
import { faFileZipper } from "@fortawesome/free-solid-svg-icons";
import {
  useCancelQuestionnaireRequest,
  useDocumentFile,
  useDownloadExportDocs,
  useDownloadExportFile,
  useExportQuestionnaireExcel,
  usePollExportStatus,
  useGetRfiResponse,
  useGetRfiResponseFile,
  useLegacyAttachmentAvailability,
  useResendQuestionnaire,
  useSupportingDocsExportVQA,
  useArchiveDocumentRequest,
  useUnarchiveDocumentRequest,
  useArchiveDocument,
  useUnarchiveDocument,
  useArchiveSponsorRfi,
  useUnarchiveSponsorRfi,
} from "../../custom-hooks/queries";
import ArchiveLibraryRequestModal from "../LibraryRequests/ArchiveLibraryRequestModal";
import ArchiveDocumentModal from "../LibraryRequests/ArchiveDocumentModal";
import { useEffect, useMemo, useRef, useState } from "react";
import Notification from "../Notification";
import moment from "moment";
import { useDecodedToken } from "../../custom-hooks/useToken";
import { useUserRole, computeRfiExportOptions, isDiligentAdminUserRole } from "../../utils/helperMethods";
import { isLibraryRequestOpenStatus } from "../../utils/libraryRequestStatusFilterOptions";
import { useRfiActionToastOptional } from "../../contexts/RfiActionToastContext";
import {
  canCancelRfiByStatus,
  canCancelRfiRequest,
  canReactivateCancelledRfi,
  canReactivateRfiRequest,
  canRequestLibraryDocAccess,
  canResendRfiInvite,
  reactivateCancelledRfiLabel,
  resolveRfiActionStatus,
  resolveRfiQuestionnaireAccepted,
} from "../../utils/rfiRequestActionPermissions";
import { useReopenVqqMenuFlow } from "../../custom-hooks/useReopenVqqFlows";
import { ReopenVqqMenuItemSlot, ReopenVqqConfirmModalSlot } from "../RFIs/ReopenVqqUiSlots";
import { resolveReopenVendorName } from "../../utils/reopenVqqUi";
import {
  isOrgVendorLibraryContext as resolveIsOrgVendorLibraryContext,
  isOrgVendorLibraryPublishedVqq as resolveIsOrgVendorLibraryPublishedVqq,
} from "../../utils/orgLibraryDocumentMenu";
import { resolveQuestionnaireExportOrgId } from "../../utils/resolveQuestionnaireExportOrgId";
import { exportSaveName } from "../../utils/exportSaveName";
import JSZip from "jszip";
import ModalWithTextBox from "../ModalWithTextBox";
import NotificationModal from "../NotificationModal";
import { useIsOrgVqaOnly } from "../../utils/vqaOnlyOrg";

/**
 * `GET Utilities/Exports/Questionnaire` must receive the same `folder` + `questionnaire_name` the
 * export worker used in Blob Storage. POST may return `file_name` with or without extension.
 */
function buildQuestionnaireExportDownloadFileName(exportFormatType, fileNameOrBase) {
  const raw = fileNameOrBase != null ? String(fileNameOrBase).trim() : "";
  const base = raw || "export";
  const lower = base.toLowerCase();
  const ext = exportFormatType === "excel" ? ".xlsx" : ".pdf";
  if (lower.endsWith(".xlsx") || lower.endsWith(".pdf")) {
    return base;
  }
  return `${base}${ext}`;
}

function buildZipExportDownloadFileName(fileNameOrBase) {
  const raw = fileNameOrBase != null ? String(fileNameOrBase).trim() : "";
  const base = raw || "export";
  if (base.toLowerCase().endsWith(".zip")) {
    return base;
  }
  return `${base}.zip`;
}

/**
 * Vendor library requests: Release / Reject — same menu shell as sponsor library (ellipsis + w-80 panel).
 */
function ProviderLibraryVendorActionsMenu({
  details,
  refetch,
  approve,
  reject,
  approveError,
  rejectError,
  rejectIsError,
}) {
  const [show, setShow] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const id = details?.request_id;

  const handleRelease = (e) => {
    e.stopPropagation();
    approve(id, {
      onSuccess: () => {
        setShow(true);
        setMessage("The Assessment request has been successfully approved.");
        refetch();
      },
      onError: () => {
        setMessage(approveError?.response?.data?.message);
        setShow(true);
      },
    });
  };

  const handleRejectClick = (e) => {
    e.stopPropagation();
    setOpen(true);
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <MenuButton
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center rounded-md p-1.5 text-sm font-medium text-gray-400 hover:text-gray-500"
        >
          <EllipsisVerticalIcon className="h-5 w-5 flex-shrink-0" />
        </MenuButton>
      </div>
      <MenuItems
        anchor="bottom end"
        portal
        className="z-50 cursor-pointer mt-2 w-80 max-h-96 overflow-y-auto rounded-md shadow-lg bg-white border border-1 ring-1 ring-black ring-opacity-5 focus:outline-none shadow-ring"
      >
        <div className="py-1">
          <MenuItem>
            {({ active }) => (
              <div
                onClick={handleRelease}
                className={`flex items-center space-x-2 ${
                  active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                } block px-4 py-2 text-sm`}
              >
                <CheckCircleIcon className="w-[16px] h-[16px]" />
                <span>Release</span>
              </div>
            )}
          </MenuItem>
          <MenuItem>
            {({ active }) => (
              <div
                onClick={handleRejectClick}
                className={`flex items-center space-x-2 ${
                  active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                } block px-4 py-2 text-sm`}
              >
                <XCircleIcon className="w-[16px] h-[16px]" />
                <span>Reject</span>
              </div>
            )}
          </MenuItem>
        </div>
      </MenuItems>
      <NotificationModal
        open={show}
        setOpen={setShow}
        desc={message}
        title={approveError || rejectIsError ? "Error" : "Success"}
        error={approveError || rejectIsError}
      />
      <ModalWithTextBox
        open={open}
        setOpen={setOpen}
        onClick={reject}
        id={id}
        refetch={refetch}
        rejectError={rejectError}
      />
    </Menu>
  );
}

export const MenuDropdownAccordingToStatuses = ({
  status,
  type,
  drawerOpen,
  docId,
  isMyRFI,
  isDocLibrary,
  questionnaireId,
  sponsorRfiResponseId,
  details,
  isLegacyRfi,
  v1_doc_id,
  v1_doc_name,
  isMyDocs,
  refetch,
  hideRequestActions = false,
  isProviderLibraryVendor = false,
  approve,
  reject,
  approveError,
  rejectError,
  rejectIsError,
  isLibraryBackendAdmin = false,
  libraryRequestId = null,
  libraryRefetch = null,
  /** Admin RFI Requests grid: show Archive/Unarchive for Sponsor RFI row */
  isRfiBackendAdmin = false,
  archiveDocumentContext = "library",
}) => {
  // Admin list DTOs may return "Rfi" / "rfi" instead of "RFI" — keep menu logic stable
  const documentTypeKey = String(type || "").toUpperCase();
  const isRfiType = documentTypeKey === "RFI";
  const isVqaOnly = useIsOrgVqaOnly();
  const isVqaType = documentTypeKey === "VQA";
  const documentTypeForApi = isRfiType || isVqaType ? documentTypeKey : type;

  const userRole = useUserRole();
  // Grid-level toast that survives the row unmount caused by refetch() (e.g. a
  // reactivated request leaves the Cancelled view). Falls back to local state if
  // no provider is present.
  const rfiActionToast = useRfiActionToastOptional();
  const isDocLibraryCatalogAdmin =
    isDiligentAdminUserRole(userRole) && isDocLibrary && !isMyDocs && !libraryRequestId;
  // Vendor org Library Documents tab lists catalog docs (document_status), not sponsor
  // request lifecycle. BUG 14491: suppress discovery actions on those Published rows.
  const isOrgVendorLibraryContext =
    resolveIsOrgVendorLibraryContext(archiveDocumentContext);
  const effectiveRfiStatus = resolveRfiActionStatus(status, details);

  // Some list endpoints (e.g. admin) return different casing for `request_status`
  const requestStatusLc = effectiveRfiStatus.toLowerCase();
  const isRequestStatus = (name) => requestStatusLc === name.toLowerCase();
  const isAnyRequestStatus = (...names) =>
    names.some((n) => requestStatusLc === n.toLowerCase());
  const isNotArchivedRequest = !isRequestStatus("Archived");
  // Catalog "Published" on the vendor org tab is completed Library content — treat like
  // Complete/Released for view + full download (not discovery "requestable").
  const isOrgVendorLibraryPublishedVqq = resolveIsOrgVendorLibraryPublishedVqq({
    archiveDocumentContext,
    documentType: type,
    isDocLibrary,
    status: effectiveRfiStatus,
    questionnaireId,
  });
  // Full downloads (Responses + Documents) become available once the vendor submits for review
  // (In Review onward). computeRfiExportOptions is the single source of truth. (14537)
  // Remap catalog Published → Complete so vendor-org Published VQQs get full downloads, not partial.
  const exportStatusForMenu = isOrgVendorLibraryPublishedVqq
    ? "Complete"
    : effectiveRfiStatus;
  const exportOptionIds = computeRfiExportOptions(exportStatusForMenu, userRole) ?? [];
  const canDownloadFullResponses = exportOptionIds.includes("EXCEL_RESPONSES");
  const isCompletedLikeDocLibraryRfi =
    canDownloadFullResponses && isRfiType && isDocLibrary && questionnaireId !== null;
  const isCompletedLikeRfiWithQuestionnaire =
    canDownloadFullResponses && isRfiType && questionnaireId !== null;

  const { mutate, isError, error } = useResendQuestionnaire();
  const [message, setMessage] = useState("");
  const { decoded, error: tokenError } = useDecodedToken();
  const canCancelThisRfi =
    isRfiType &&
    !hideRequestActions &&
    !isOrgVendorLibraryContext &&
    canCancelRfiRequest(userRole) &&
    canCancelRfiByStatus(effectiveRfiStatus);
  const canReactivateThisRfi =
    isRfiType &&
    !hideRequestActions &&
    canReactivateRfiRequest(userRole) &&
    canReactivateCancelledRfi(effectiveRfiStatus);
  const reactivateMenuLabel = reactivateCancelledRfiLabel(details, userRole);
  // Partial exports pull the vendor's in-progress HyperComply copy, which only exists once the
  // vendor has accepted — so gate on acceptance. A re-opened, not-yet-accepted VQQ (back to Sent)
  // must not surface partial download.
  const showPartialExports =
    isRfiType &&
    questionnaireId !== null &&
    !isOrgVendorLibraryContext &&
    exportOptionIds.includes("EXCEL_PARTIAL") &&
    resolveRfiQuestionnaireAccepted(details);

  const {
    mutate: cancelMutate,
    isError: cancelError,
    error: cancelErrorMessage,
  } = useCancelQuestionnaireRequest();

  const [showNotification, setShowNotification] = useState(false);
  /** Drives toast style; resend/cancel `isError` is unrelated to archive API failures */
  const [localNotificationError, setLocalNotificationError] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Drives the spinner shown on the row's three-dot button while a partial export is in flight.
  // Partial export takes 10s+ (worker generation + retry polling), so the user needs visible
  // feedback at the row level — not just inside the dropdown.
  const [isExportingPartial, setIsExportingPartial] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  /** 'library' | 'rfi' | 'document' — which API to call on confirm */
  const [archiveModalTarget, setArchiveModalTarget] = useState(null);
  const [documentArchiveModalOpen, setDocumentArchiveModalOpen] = useState(false);
  const [documentArchiveModalMode, setDocumentArchiveModalMode] = useState("archive");

  const { mutate: archiveMutate, isPending: archivePending } =
    useArchiveDocumentRequest();
  const { mutate: unarchiveMutate, isPending: unarchivePending } =
    useUnarchiveDocumentRequest();
  const { mutate: archiveDocumentMutate, isPending: archiveDocumentPending } =
    useArchiveDocument();
  const { mutate: unarchiveDocumentMutate, isPending: unarchiveDocumentPending } =
    useUnarchiveDocument();
  const { mutate: archiveRfiMutate, isPending: archiveRfiPending } =
    useArchiveSponsorRfi();
  const { mutate: unarchiveRfiMutate, isPending: unarchiveRfiPending } =
    useUnarchiveSponsorRfi();

  const effectiveSponsorRfiResponseId =
    sponsorRfiResponseId ??
    details?.entity_id ??
    details?.sponsorRfiResponseId ??
    details?.SponsorRfiResponseId;

  const resolvedCancelQuestionnaireId = useMemo(() => {
    const fromProp = Number(questionnaireId);
    if (Number.isFinite(fromProp) && fromProp > 0) return fromProp;

    const rawId = isDocLibrary
      ? details?.hc_questionnaire_id
      : details?.questionnaire_id ?? details?.questionnaireId;
    const fromDetails = Number(rawId);
    if (Number.isFinite(fromDetails) && fromDetails > 0) return fromDetails;

    return null;
  }, [questionnaireId, isDocLibrary, details]);

  const reopenFlow = useReopenVqqMenuFlow({
    status: effectiveRfiStatus, details, userRole,
    questionnaireId: resolvedCancelQuestionnaireId,
    enabled: isRfiType && !hideRequestActions && !isOrgVendorLibraryContext,
    rfiActionToast, setLocalNotificationError, setShowNotification, setMessage, refetch,
  });

  const {
    mutate: getFile,
    data: files,
    isError: isFileError,
    error: errorMessageFile,
  } = useDocumentFile();

  const {
    mutate: downloadSupportDocs,
    data: supportDocs,
    isError: isSupportDocs,
    error: errorSupportDocs,
  } = useSupportingDocsExportVQA();

  const fileDownload = () => {
    // A VQA card may be a placeholder row; the API names the released report to download.
    getFile(details?.download_document_id ?? docId);
  };

  const { mutate: exportExcel } = useExportQuestionnaireExcel();
  const {
    mutate: exportToExcel,
    data: exportData,
    isError: exportExcelError,
  } = useDownloadExportFile();

  const {
    mutate: exportToDocs,
    data: exportDocsData,
    isError: exportDocsError,
  } = useDownloadExportDocs();

  const legacyAttachmentsQueryEnabled =
    isLegacyRfi &&
    !!v1_doc_id &&
    !!v1_doc_name &&
    ((isRfiType && isRequestStatus("Released")) ||
      (isRequestStatus("Complete") && isMyRFI));

  const { data: legacyAttachmentAvailability } =
    useLegacyAttachmentAvailability(
      v1_doc_id,
      v1_doc_name,
      legacyAttachmentsQueryEnabled,
    );

  const hasLegacyAttachments =
    legacyAttachmentAvailability?.has_prebuilt_zip ||
    legacyAttachmentAvailability?.has_loose_attachments;

  const { mutate: getRfiResponseFile, data: responseFileData } =
    useGetRfiResponseFile();
  const { mutate: getRfiResponse } = useGetRfiResponse();

  const [exportLogsId, setExportLogsId] = useState(null);
  const [pendingDownloadType, setPendingDownloadType] = useState(null);
  const [pendingDownloadPayload, setPendingDownloadPayload] = useState(null);
  // The file name the export returned; downloads are saved under it (see exportSaveName).
  const [exportFileName, setExportFileName] = useState(null);

  const { data: exportStatusData } = usePollExportStatus(exportLogsId, {
    enabled: !!exportLogsId,
  });

  useEffect(() => {
    if (exportStatusData?.status !== "Completed" || !pendingDownloadPayload)
      return;

    const onDone = () => {
      setMessage("Your download has started.");
      setShowNotification(true);
      setIsExporting(false);
      setIsExportingPartial(false);
      setExportLogsId(null);
      setPendingDownloadType(null);
      setPendingDownloadPayload(null);
    };
    const onFail = (error) => {
      console.error("[MenuDropdown] download after poll failed:", error);
      setIsExporting(false);
      setIsExportingPartial(false);
      setExportLogsId(null);
      setPendingDownloadType(null);
      setPendingDownloadPayload(null);
    };

    if (pendingDownloadType === "zip") {
      exportToDocs(pendingDownloadPayload, { onSuccess: onDone, onError: onFail });
    } else {
      exportToExcel(pendingDownloadPayload, { onSuccess: onDone, onError: onFail });
    }
  }, [exportStatusData?.status]);

  useEffect(() => {
    if (exportStatusData?.status !== "Error") return;
    console.error(
      "[MenuDropdown] Export failed on server:",
      exportStatusData?.error_description
    );
    setExportLogsId(null);
    setPendingDownloadType(null);
    setPendingDownloadPayload(null);
    setIsExporting(false);
    setIsExportingPartial(false);
  }, [exportStatusData?.status]);

  const handleExportExcel = (type) => {
    const vendorName = isDocLibrary
      ? details?.provider_name
      : details?.vendorName;
    const id = isDocLibrary
      ? details?.hc_questionnaire_id
      : details?.questionnaire_id;

    const sponsorName = isDocLibrary
      ? details?.sponsor_name ||
          details?.sponsorName ||
          decoded?.sponsor_name
      : details?.sponsorName;
    const org_id = resolveQuestionnaireExportOrgId(isDocLibrary, details, decoded);

    const questionnaireName = isDocLibrary
      ? `Diligent-${
          isMyDocs ? details?.capabilities[0]?.rfi_name : details?.document_name
        }-${details?.requested_on}`
      : details?.name;

    const encodedQuestionnaireName = encodeURIComponent(questionnaireName);

    const data = {
      entity_id: id,
      entity_type: "QuestionnaireResponse",
      purchase_vendor: vendorName || "",
      sponsor_name: sponsorName || "",
      export_format: type,
      org_id,
      questionnaire_name: encodedQuestionnaireName,
      released_date: moment().toISOString(),
      subtype: "test",
    };

    setIsExporting(true);
    exportExcel(data, {
      onSuccess: (response) => {
        const folder = isDocLibrary
          ? details?.hc_questionnaire_id
          : details?.questionnaire_id;
        // BE returns file_name: vendor, template, release date and the location while it fits,
        // within 70 characters. The Diligent-* name is only a fallback for library rows.
        const questionnaireNames = isDocLibrary
          ? `Diligent-${
              isMyDocs
                ? details?.capabilities[0]?.rfi_name
                : details?.document_name
            }-${details?.requested_on}`
          : response?.file_name || details?.name;
        const downloadBaseName =
          response?.file_name || questionnaireNames || "export";
        const downloadPayload = {
          folder,
          questionnaire_name: buildQuestionnaireExportDownloadFileName(
            type,
            downloadBaseName,
          ),
        };
        setExportLogsId(response?.export_logs_id);
        setPendingDownloadType("excel");
        setExportFileName(response?.file_name ?? null);
        setPendingDownloadPayload(downloadPayload);
      },
      onError: (error) => {
        console.error("[MenuDropdown] Export Excel failed:", {
          status: error?.response?.status,
          data: error?.response?.data,
          message: error?.message,
        });
        setIsExporting(false);
      },
    });
  };

  const handleRFILegacy = (type, doc_id, docName) => {
    const extentions = type === "excel" ? ".xlsx" : ".pdf";
    exportToExcel(
      {
        folder: v1_doc_id,
        questionnaire_name: `${v1_doc_name}${extentions}`,
      },
      {
        onSuccess: (response) => {
          setMessage("Your download has started.");
          setShowNotification(true);
        },
        onError: (error) => {
          console.log(error);
        },
      },
    );
  };

  const handleRFILegacyAttachments = () => {
    exportToDocs(
      {
        folder: v1_doc_id,
        questionnaire_name: buildZipExportDownloadFileName(
          `${v1_doc_name}_attachments`,
        ),
        exclude_response_base_name: v1_doc_name,
      },
      {
        onSuccess: () => {
          setMessage("Your download has started.");
          setShowNotification(true);
        },
        onError: (error) => {
          console.error("[MenuDropdown] legacy attachments download failed:", error);
        },
      },
    );
  };

  const handleV1Docs = (type) => {
    const extentions = type === "excel" ? "xlsx" : "pdf";

    // Show download started message immediately when button is clicked
    setMessage("Download has started, it may take a while");
    setShowNotification(false); // Clear any existing notification
    setIsDownloading(true);

    getRfiResponse(v1_doc_id, {
      onSuccess: (response) => {
        // Add 10-second delay before calling getRfiResponseFile
        setTimeout(() => {
          let retryCount = 0;
          const maxRetries = 3;

          const attemptGetRfiResponseFile = () => {
            getRfiResponseFile(
              { rfiResponseId: v1_doc_id, extension: extentions },
              {
                onSuccess: (fileResponse) => {
                  // Success - update message to indicate completion
                  setIsDownloading(false);
                  setMessage("File download completed successfully");
                  setShowNotification(true);
                },
                onError: (error) => {
                  retryCount++;

                  if (retryCount < maxRetries) {
                    // Update message to show retry attempt
                    setMessage(
                      `Download in progress... Retry attempt ${
                        retryCount + 1
                      } of ${maxRetries}`,
                    );
                    // Retry after a short delay (1 second)
                    setTimeout(() => {
                      attemptGetRfiResponseFile();
                    }, 1000);
                  } else {
                    // All retries failed - show error message
                    setIsDownloading(false);
                    setMessage(
                      error?.response?.data?.message ||
                        "Failed to download file after multiple attempts. Please try again.",
                    );
                    setShowNotification(true);
                  }
                },
              },
            );
          };

          // Start the first attempt
          attemptGetRfiResponseFile();
        }, 10000); // 10 seconds delay
      },
      onError: (error) => {
        setIsDownloading(false);
        setMessage(
          error?.response?.data?.message ||
            "Failed to get VQQ response. Please try again.",
        );
        setShowNotification(true);
      },
    });
  };

  // Pre-completion partial export: vendor/admin grabs the RFI as Excel/PDF with whatever
  // responses have been entered in HC so far. Same payload as handleExportExcel plus is_partial.
  const handleExportPartial = (type) => {
    const extentions = type === "excel" ? ".xlsx" : ".pdf";
    const vendorName = isDocLibrary ? details?.provider_name : details?.vendorName;
    const id = isDocLibrary ? details?.hc_questionnaire_id : details?.questionnaire_id;
    const sponsorName = isDocLibrary
      ? details?.sponsor_name || details?.sponsorName || decoded?.sponsor_name
      : details?.sponsorName;
    const org_id = resolveQuestionnaireExportOrgId(isDocLibrary, details, decoded);
    const questionnaireName = isDocLibrary
      ? `Diligent-${
          isMyDocs ? details?.capabilities[0]?.rfi_name : details?.document_name
        }-${details?.requested_on}`
      : details?.name;
    const encodedQuestionnaireName = encodeURIComponent(questionnaireName);

    const data = {
      entity_id: id,
      entity_type: "QuestionnaireResponse",
      purchase_vendor: vendorName,
      sponsor_name: sponsorName,
      export_format: type,
      org_id: org_id,
      questionnaire_name: encodedQuestionnaireName,
      released_date: moment().toISOString(),
      subtype: "test",
      is_partial: true,
    };

    setIsExportingPartial(true);
    exportExcel(data, {
      onSuccess: (response) => {
        const folder = isDocLibrary
          ? details?.hc_questionnaire_id
          : details?.questionnaire_id;
        const questionnaireNames = isDocLibrary
          ? `Diligent-${
              isMyDocs
                ? details?.capabilities[0]?.rfi_name
                : details?.document_name
            }-${details?.requested_on}`
          : response?.file_name || details?.name;
        const downloadPayload = {
          folder,
          questionnaire_name: `${questionnaireNames || "export"}${extentions}`,
        };
        setExportLogsId(response?.export_logs_id);
        setPendingDownloadType("partial");
        setExportFileName(response?.file_name ?? null);
        setPendingDownloadPayload(downloadPayload);
      },
      onError: (error) => {
        console.error("[MenuDropdown] Export Partial failed:", {
          status: error?.response?.status,
          message: error?.message,
        });
        setIsExportingPartial(false);
      },
    });
  };

  const handleAllDocuments = (type) => {
    const vendorName = isDocLibrary
      ? details?.provider_name
      : details?.vendorName;
    const id = isDocLibrary
      ? details?.hc_questionnaire_id
      : details?.questionnaire_id;

    const sponsorName = isDocLibrary
      ? details?.sponsor_name ||
          details?.sponsorName ||
          decoded?.sponsor_name
      : details?.sponsorName;
    const org_id = resolveQuestionnaireExportOrgId(isDocLibrary, details, decoded);

    const questionnaireName = isDocLibrary
      ? `Diligent-${details?.document_name}-${details?.requested_on}`
      : details?.name;

    const encodedQuestionnaireName = encodeURIComponent(questionnaireName);

    // BE recognizes only "test" (responses-only file) and "alldocuments"
    // (ZIP containing the responses export plus every supporting document
    // attached to the questionnaire). Anything else falls back to the
    // responses-only path, which is why this menu was returning a ZIP with
    // just the response file and no extra documents.
    const data = {
      entity_id: id,
      entity_type: "QuestionnaireResponse",
      purchase_vendor: vendorName,
      sponsor_name: sponsorName,
      export_format: type,
      org_id,
      questionnaire_name: encodedQuestionnaireName,
      released_date: moment().toISOString(),
      subtype: "alldocuments",
    };

    setIsExporting(true);
    exportExcel(data, {
      onSuccess: (response) => {
        const folder = isDocLibrary
          ? details?.hc_questionnaire_id
          : details?.questionnaire_id;
        // BE returns file_name: vendor, template, release date and the location while it fits,
        // within 70 characters. The Diligent-* name is only a fallback for library rows.
        const questionnaireNames = isDocLibrary
          ? `Diligent-${details?.document_name}-${details?.requested_on}`
          : response?.file_name || details?.name;
        const zipBaseName =
          response?.file_name || questionnaireNames || "export";
        const downloadPayload = {
          folder,
          questionnaire_name: buildZipExportDownloadFileName(zipBaseName),
        };
        setExportLogsId(response?.export_logs_id);
        setPendingDownloadType("zip");
        setExportFileName(response?.file_name ?? null);
        setPendingDownloadPayload(downloadPayload);
      },
      onError: (error) => {
        console.error("Export Excel failed:", error);
        setIsExporting(false);
      },
    });
  };

  const lastZipDownloadedRef = useRef(null);

  useEffect(() => {
    const downloadLink = exportDocsData?.download_link;

    if (!downloadLink || exportExcelError) return;
    if (lastZipDownloadedRef.current === downloadLink) return;
    lastZipDownloadedRef.current = downloadLink;

    fetch(downloadLink)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");

        const contentDisposition = res.headers.get("Content-Disposition");
        let fileName = "document";

        if (contentDisposition?.includes("filename=")) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match?.[1]) {
            fileName = match[1].split(".")[0];
          }
        }

        return res.blob().then((blob) => ({ blob, fileName }));
      })
      .then(({ blob, fileName }) => {
        const questionnaireName = exportSaveName({ isDocLibrary, details, exportFileName });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${questionnaireName || fileName}.zip`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("ZIP download failed:", error);
      });
  }, [exportDocsData?.download_link, exportExcelError]);

  const lastExportDownloadedRef = useRef(null);

  useEffect(() => {
    const downloadLink = exportData?.download_link;

    if (!downloadLink || exportExcelError) return;
    if (lastExportDownloadedRef.current === downloadLink) return;
    lastExportDownloadedRef.current = downloadLink;

    fetch(downloadLink)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");

        const contentDisposition = res.headers.get("Content-Disposition");
        const contentType = res.headers.get("Content-Type");

        let fileName = "document";
        let fileExtension = "xlsx"; // default

        if (contentDisposition?.includes("filename=")) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match?.[1]) {
            const parts = match[1].split(".");
            fileName = parts[0];
            fileExtension = parts[1] || "xlsx";
          }
        } else if (downloadLink.includes("?")) {
          const match = downloadLink.match(/\.([a-zA-Z0-9]+)(\?|$)/);
          if (match?.[1]) {
            fileExtension = match[1];
          }
        }

        return res.blob().then((blob) => ({ blob, fileName, fileExtension }));
      })
      .then(({ blob, fileName, fileExtension }) => {
        const url = window.URL.createObjectURL(blob);
        const questionnaireName = exportSaveName({ isDocLibrary, details, exportFileName });
        const link = document.createElement("a");
        link.href = url;
        link.download = `${questionnaireName || fileName}.${fileExtension}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("File download failed:", error);
      });
  }, [exportData?.download_link, exportExcelError]);

  const lastRfiResponseFileDownloadedRef = useRef(null);

  useEffect(() => {
    const downloadLink = responseFileData?.download_link;

    if (!downloadLink) return;
    if (lastRfiResponseFileDownloadedRef.current === downloadLink) return;
    lastRfiResponseFileDownloadedRef.current = downloadLink;

    // Extract filename from URL before the query parameters
    const extractFileNameFromUrl = (url) => {
      try {
        // Remove query parameters and get the pathname
        const urlWithoutQuery = url.split("?")[0];
        // Extract the last part of the path (filename)
        const pathParts = urlWithoutQuery.split("/");
        const fileName = pathParts[pathParts.length - 1];

        if (fileName && fileName.includes(".")) {
          const parts = fileName.split(".");
          const name = parts.slice(0, -1).join(".");
          const extension = parts[parts.length - 1];
          return { fileName: name, fileExtension: extension };
        }
        return {
          fileName: fileName || "rfi_response_file",
          fileExtension: "pdf",
        };
      } catch (error) {
        console.error("Error extracting filename from URL:", error);
        return { fileName: "rfi_response_file", fileExtension: "pdf" };
      }
    };

    const { fileName: extractedFileName, fileExtension: extractedExtension } =
      extractFileNameFromUrl(downloadLink);

    fetch(downloadLink)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");

        const contentDisposition = res.headers.get("Content-Disposition");
        let fileName = extractedFileName;
        let fileExtension = extractedExtension;

        // If Content-Disposition header has filename, use that instead
        if (contentDisposition?.includes("filename=")) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match?.[1]) {
            const parts = match[1].split(".");
            fileName = parts[0];
            fileExtension = parts[1] || extractedExtension;
          }
        }

        return res.blob().then((blob) => ({ blob, fileName, fileExtension }));
      })
      .then(({ blob, fileName, fileExtension }) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${fileName}.${fileExtension}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show completion message
        setIsDownloading(false);
        setMessage("File download completed successfully");
        setShowNotification(true);
      })
      .catch((error) => {
        console.error("RFI Response file download failed:", error);
        setIsDownloading(false);
        setMessage("Failed to download file. Please try again.");
        setShowNotification(true);
      });
  }, [responseFileData?.download_link]);

  // Function to extract filename from URL
  const extractFilenameFromUrl = (url) => {
    try {
      // Remove query parameters and get the pathname
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;

      // Extract filename from pathname
      const filename = pathname.split("/").pop();

      // Decode URL-encoded characters
      return decodeURIComponent(filename);
    } catch (error) {
      console.error("Error extracting filename from URL:", error);
      return "file.pdf"; // Fallback filename
    }
  };

  useEffect(() => {
    if (isFileError) {
      return;
    }

    if (files?.download_link) {
      // Extract filename from the URL
      const filename = extractFilenameFromUrl(files.download_link);

      // Fetch the file and trigger download
      fetch(files.download_link)
        .then((response) => {
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          return response.blob(); // Convert the response to a Blob
        })
        .then((blob) => {
          const url = URL.createObjectURL(blob); // Create an Object URL
          const a = document.createElement("a");
          a.style.display = "none"; // Hide the anchor element
          a.href = url;
          a.download = filename; // Use extracted filename for download
          document.body.appendChild(a);
          a.click(); // Programmatically trigger the click
          document.body.removeChild(a); // Clean up
          URL.revokeObjectURL(url); // Release the Object URL
        })
        .catch((error) => {
          console.error("Failed to download file:", error);
        });
    }
  }, [files, isFileError]);

  const navigate = useNavigate();

  const handleSupportingDocsDownloadVQA = () => {
    downloadSupportDocs({
      entityId: sponsorRfiResponseId,
      entityType: documentTypeForApi,
    });
  };

  useEffect(() => {
    if (errorSupportDocs) {
      setShowNotification(true);
      return;
    }

    if (supportDocs?.items?.length) {
      const zip = new JSZip();
      const promises = supportDocs?.items.map((file, index) =>
        fetch(file.document_url)
          .then((res) => res.blob())
          .then((blob) => {
            const documentName = file.document_name;
            zip.file(documentName, blob);
          }),
      );

      Promise.all(promises).then(() => {
        zip.generateAsync({ type: "blob" }).then((zipBlob) => {
          const zipLink = document.createElement("a");
          zipLink.href = URL.createObjectURL(zipBlob);
          zipLink.setAttribute(
            "download",
            `DiligentPharmaAssessment-${moment().format("YYYY-MM-DD")}.zip`,
          ); // ZIP filename
          document.body.appendChild(zipLink);
          zipLink.click();
          document.body.removeChild(zipLink);
        });
      });
    }
  }, [supportDocs, errorSupportDocs]);

  const menuHasActions = useMemo(() => {
    if (isProviderLibraryVendor) return true;
    const lc = String(effectiveRfiStatus ?? "").trim().toLowerCase();
    const req = (n) => lc === n.toLowerCase();
    const any = (...names) => names.some((n) => lc === n.toLowerCase());
    const notArchived = !req("archived");

    const releasedOrCompleteRfiWithQuestionnaire =
      (any("complete", "released") ||
        (isOrgVendorLibraryContext && any("published"))) &&
      isRfiType &&
      questionnaireId !== null;

    return (
      (isRfiType && isMyRFI && !isLegacyRfi) ||
      (any("complete", "released") && isRfiType && isMyRFI && !isLegacyRfi) ||
      (releasedOrCompleteRfiWithQuestionnaire && isDocLibrary) ||
      releasedOrCompleteRfiWithQuestionnaire ||
      // Document Library discovery: Published/Cancelled → Request Access (not vendor org tab)
      (any("published", "cancelled") &&
        isRfiType &&
        isDocLibrary &&
        !isOrgVendorLibraryContext) ||
      (req("pending") && isRfiType && isDocLibrary) ||
      (req("sent") && isRfiType) ||
      (canReactivateThisRfi && isRfiType) ||
      (reopenFlow.canReopen && isRfiType) ||
      (canCancelThisRfi && isRfiType) ||
      (any("pending", "sent") && isRfiType && !hideRequestActions) ||
      (req("in progress") && isRfiType && isDocLibrary) ||
      (req("released") && isVqaType && isDocLibrary) ||
      (req("published") &&
        isVqaType &&
        isDocLibrary &&
        !hideRequestActions &&
        !isOrgVendorLibraryContext) ||
      (req("upcoming") &&
        isVqaType &&
        isDocLibrary &&
        !hideRequestActions &&
        !isOrgVendorLibraryContext) ||
      (isLegacyRfi && isRfiType && req("released")) ||
      (isLegacyRfi && isRfiType && req("complete") && isMyRFI) ||
      (isLibraryBackendAdmin &&
        isDocLibrary &&
        isMyDocs &&
        libraryRequestId &&
        req("archived")) ||
      (isLibraryBackendAdmin &&
        isDocLibrary &&
        isMyDocs &&
        libraryRequestId &&
        notArchived) ||
      (isRfiBackendAdmin &&
        isRfiType &&
        !isDocLibrary &&
        effectiveSponsorRfiResponseId &&
        !isLegacyRfi &&
        req("archived")) ||
      (isRfiBackendAdmin &&
        isRfiType &&
        !isDocLibrary &&
        effectiveSponsorRfiResponseId &&
        !isLegacyRfi &&
        notArchived) ||
      (isDocLibraryCatalogAdmin && isRequestStatus("Archived")) ||
      (isDocLibraryCatalogAdmin && isNotArchivedRequest)
    );
  }, [
    isProviderLibraryVendor,
    effectiveRfiStatus,
    isRfiType,
    isVqaType,
    isMyRFI,
    isLegacyRfi,
    isDocLibrary,
    questionnaireId,
    hideRequestActions,
    isLibraryBackendAdmin,
    isMyDocs,
    libraryRequestId,
    isDocLibraryCatalogAdmin,
    isRfiBackendAdmin,
    effectiveSponsorRfiResponseId,
    canCancelThisRfi,
    canReactivateThisRfi,
    reopenFlow.canReopen,
    isOrgVendorLibraryContext,
  ]);

  if (isProviderLibraryVendor) {
    return (
      <ProviderLibraryVendorActionsMenu
        details={details}
        refetch={refetch}
        approve={approve}
        reject={reject}
        approveError={approveError}
        rejectError={rejectError}
        rejectIsError={rejectIsError}
      />
    );
  }

  // A VQA only org has nothing to do with VQQs, including the ones it had before.
  if (!menuHasActions || (isVqaOnly && isRfiType)) {
    return null;
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <MenuButton
          onClick={(e) => e.stopPropagation()}
          disabled={isExporting || isExportingPartial}
          title={
            isExporting || isExportingPartial
              ? "Preparing your export..."
              : undefined
          }
          className="inline-flex items-center justify-center rounded-md p-1.5 text-sm font-medium text-gray-400 hover:text-gray-500 disabled:cursor-wait"
        >
          {isExporting || isExportingPartial ? (
            <ArrowPathIcon className="h-5 w-5 flex-shrink-0 animate-spin text-indigo-600" />
          ) : (
            <EllipsisVerticalIcon className="h-5 w-5 flex-shrink-0" />
          )}
        </MenuButton>
      </div>
      {/* Dropdown menu */}
      <MenuItems
        anchor="bottom end"
        portal
        className="z-50 cursor-pointer mt-2 w-80 max-h-96 overflow-y-auto rounded-md shadow-lg bg-white border border-1 ring-1 ring-black ring-opacity-5 focus:outline-none shadow-ring"
      >
        <div className="py-1">
          {isRfiType && isMyRFI && !isLegacyRfi && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/RFI/${questionnaireId}/Details`);
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  {" "}
                  <EyeIcon className="w-[16px] h-[16px]" />
                  <span>View Details</span>{" "}
                </div>
              )}
            </MenuItem>
          )}
          {isAnyRequestStatus("Complete", "Released") &&
            isRfiType &&
            isMyRFI &&
            !isLegacyRfi && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/RFI/${questionnaireId}/Details?page=responses`,
                      );
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    {" "}
                    <EyeIcon className="w-[16px] h-[16px]" />
                    <span>View Responses</span>{" "}
                  </div>
                )}
              </MenuItem>
            )}
          <ReopenVqqMenuItemSlot flow={reopenFlow} />
          {isCompletedLikeDocLibraryRfi && resolvedCancelQuestionnaireId && (
              <Menu.Item>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/RFI/${resolvedCancelQuestionnaireId}/Details`);
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <ViewfinderCircleIcon className="w-[16px] h-[16px]" />
                    <span>View Details</span>
                  </div>
                )}
              </Menu.Item>
            )}
          {isCompletedLikeDocLibraryRfi && resolvedCancelQuestionnaireId && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/RFI/${resolvedCancelQuestionnaireId}/Details?page=responses`,
                      );
                    }}
                    className={` flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <ViewColumnsIcon className="w-[16px] h-[16px]" />{" "}
                    <span>View Responses</span>
                  </div>
                )}
              </MenuItem>
            )}
          {showPartialExports && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExportPartial("excel");
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFileExcel}
                    className="w-[16px] h-[16px] text-green-700"
                  />
                  <span>Excel - Partially complete</span>
                </div>
              )}
            </MenuItem>
          )}
          {showPartialExports && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExportPartial("pdf");
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFilePdf}
                    className="w-[16px] h-[16px] text-red-700"
                  />
                  <span>PDF - Partially complete</span>
                </div>
              )}
            </MenuItem>
          )}
          {isCompletedLikeRfiWithQuestionnaire && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExportExcel("excel");
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <FontAwesomeIcon
                      icon={faFileExcel}
                      className="w-[16px] h-[16px] text-green-700"
                    />
                    <span>Excel - Responses only</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isCompletedLikeRfiWithQuestionnaire && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExportExcel("pdf");
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <FontAwesomeIcon
                      icon={faFilePdf}
                      className="w-[16px] h-[16px] text-red-700"
                    />
                    <span>PDF - Responses only</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isCompletedLikeRfiWithQuestionnaire && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAllDocuments("excel");
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <FontAwesomeIcon
                      icon={faFileZipper}
                      className="w-[16px] h-[16px] text-rose-700"
                    />
                    <span>Responses + Documents in Excel</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isCompletedLikeRfiWithQuestionnaire && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAllDocuments("pdf");
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <FontAwesomeIcon
                      icon={faFileZipper}
                      className="w-[16px] h-[16px] text-rose-700"
                    />
                    <span>Responses + Documents in PDF</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isAnyRequestStatus("Published", "Cancelled") &&
            isRfiType &&
            isDocLibrary &&
            canRequestLibraryDocAccess(userRole) &&
            !isOrgVendorLibraryContext && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      drawerOpen();
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <ArrowPathRoundedSquareIcon className="w-[16px] h-[16px]" />{" "}
                    <span>Request Access</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isLibraryRequestOpenStatus(effectiveRfiStatus) &&
            isRfiType &&
            isDocLibrary &&
            resolvedCancelQuestionnaireId && (
            <Menu.Item>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/RFI/${resolvedCancelQuestionnaireId}/Details`);
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <ViewfinderCircleIcon className="w-[16px] h-[16px]" />
                  <span>View Details</span>
                </div>
              )}
            </Menu.Item>
          )}
          {isRequestStatus("Sent") && isRfiType && canResendRfiInvite(userRole) && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    mutate(
                      { id: questionnaireId },
                      {
                        onSuccess: () => {
                          setLocalNotificationError(false);
                          setShowNotification(true);
                          setMessage("Request resend successfully");
                          refetch?.();
                        },
                        onError: (error) => {
                          setLocalNotificationError(true);
                          setShowNotification(true);
                          setMessage(
                            error?.response?.data?.message ||
                              "Error resending request",
                          );
                        },
                      },
                    );
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <EnvelopeIcon className="w-[16px] h-[16px]" />
                  <span>Resend Invite</span>
                </div>
              )}
            </MenuItem>
          )}

          {canReactivateThisRfi && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    mutate(
                      { id: questionnaireId },
                      {
                        onSuccess: () => {
                          // Show via the grid-level provider so the toast is not torn
                          // down when refetch() unmounts this row's menu.
                          if (rfiActionToast) {
                            rfiActionToast.showToast({
                              message: "Request reactivated successfully",
                              status: "success",
                            });
                          } else {
                            setLocalNotificationError(false);
                            setShowNotification(true);
                            setMessage("Request reactivated successfully");
                          }
                          refetch?.();
                        },
                        onError: (error) => {
                          setLocalNotificationError(true);
                          setShowNotification(true);
                          setMessage(
                            error?.response?.data?.message ||
                              "Error reactivating request",
                          );
                        },
                      },
                    );
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <EnvelopeIcon className="w-[16px] h-[16px]" />
                  <span>{reactivateMenuLabel}</span>
                </div>
              )}
            </MenuItem>
          )}

          {canCancelThisRfi && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        !resolvedCancelQuestionnaireId &&
                        !effectiveSponsorRfiResponseId
                      ) {
                        setLocalNotificationError(true);
                        setShowNotification(true);
                        setMessage("Questionnaire id is missing for this VQQ.");
                        return;
                      }
                      cancelMutate(
                        {
                          id: resolvedCancelQuestionnaireId,
                          sponsorRfiResponseId: effectiveSponsorRfiResponseId,
                        },
                        {
                          onSuccess: () => {
                            setLocalNotificationError(false);
                            setShowNotification(true);
                            setMessage("Request cancelled successfully");
                            refetch?.();
                          },
                          onError: (error) => {
                            setLocalNotificationError(true);
                            setShowNotification(true);
                            setMessage(
                              error?.response?.data?.message ||
                                error?.message ||
                                "Error cancelling request",
                            );
                          },
                        },
                      );
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm text-red-700`}
                  >
                    {" "}
                    <XCircleIcon className="w-[16px] h-[16px]" />
                    <span>Cancel Request</span>{" "}
                  </div>
                )}
              </MenuItem>
            )}
          {isRequestStatus("In Progress") && isRfiType && isDocLibrary && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className={`${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  View Questionniare{" "}
                </div>
              )}
            </MenuItem>
          )}

          {isRequestStatus("Released") && isVqaType && isDocLibrary && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    fileDownload();
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFile}
                    className="w-[16px] h-[16px] text-gray-700"
                  />
                  <span>Download Report</span>
                </div>
              )}
            </MenuItem>
          )}
          {/* {isRequestStatus("Released") && isVqaType && isDocLibrary && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSupportingDocsDownloadVQA();
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFileZipper}
                    className="w-[16px] h-[16px] text-rose-700"
                  />
                  <span>Download Report + Supporting Documents</span>
                </div>
              )}
            </MenuItem>
          )} */}
          {isRequestStatus("Published") &&
            isVqaType &&
            isDocLibrary &&
            !hideRequestActions &&
            !isOrgVendorLibraryContext && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      drawerOpen();
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <ArrowPathRoundedSquareIcon className="w-[16px] h-[16px]" />{" "}
                    <span>Request access</span>
                  </div>
                )}
              </MenuItem>
            )}
          {/* {status === "Pending" && isVqaType && isDocLibrary && !hideRequestActions && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <XCircleIcon className="w-[16px] h-[16px]" />
                  <span>Cancel Request</span>{" "}
                </div>
              )}
            </MenuItem>
          )} */}
          {isRequestStatus("Upcoming") &&
            isVqaType &&
            isDocLibrary &&
            !hideRequestActions &&
            !isOrgVendorLibraryContext && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      drawerOpen();
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <ArrowPathRoundedSquareIcon className="w-[16px] h-[16px]" />{" "}
                    <span>Request access</span>
                  </div>
                )}
              </MenuItem>
            )}

          {isLegacyRfi && isRfiType && isRequestStatus("Released") && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRFILegacy("excel");
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFileExcel}
                    className="w-[16px] h-[16px] text-green-700"
                  />
                  <span>Excel - Responses only</span>
                </div>
              )}
            </MenuItem>
          )}
          {isLegacyRfi && isRfiType && isRequestStatus("Released") && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRFILegacy("pdf");
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFilePdf}
                    className="w-[16px] h-[16px] text-red-700"
                  />
                  <span>PDF - Responses only</span>
                </div>
              )}
            </MenuItem>
          )}
          {isLegacyRfi && isRequestStatus("Complete") && isMyRFI && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRFILegacy("excel");
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFileExcel}
                    className="w-[16px] h-[16px] text-green-700"
                  />
                  <span>Excel - Responses only</span>
                </div>
              )}
            </MenuItem>
          )}
          {isLegacyRfi && isRequestStatus("Complete") && isMyRFI && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRFILegacy("pdf");
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFilePdf}
                    className="w-[16px] h-[16px] text-red-700"
                  />
                  <span>PDF - Responses only</span>
                </div>
              )}
            </MenuItem>
          )}
          {isLegacyRfi && isRfiType && isRequestStatus("Released") && hasLegacyAttachments && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRFILegacyAttachments();
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFileZipper}
                    className="w-[16px] h-[16px] text-rose-700"
                  />
                  <span>Download Attachments</span>
                </div>
              )}
            </MenuItem>
          )}
          {isLegacyRfi && isRequestStatus("Complete") && isMyRFI && hasLegacyAttachments && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRFILegacyAttachments();
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <FontAwesomeIcon
                    icon={faFileZipper}
                    className="w-[16px] h-[16px] text-rose-700"
                  />
                  <span>Download Attachments</span>
                </div>
              )}
            </MenuItem>
          )}
          {/* {status === "Upcoming" && isVqaType && isDocLibrary && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/Assessment/Calendar");
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  {" "}
                  <ArrowPathRoundedSquareIcon className="w-[16px] h-[16px]" />{" "}
                  <span>Join Assessment</span>{" "}
                </div>
              )}
            </MenuItem>
          )}  */}
          {isLibraryBackendAdmin &&
            isDocLibrary &&
            isMyDocs &&
            libraryRequestId &&
            isRequestStatus("Archived") && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      unarchiveMutate(libraryRequestId, {
                        onSuccess: () => {
                          setLocalNotificationError(false);
                          setShowNotification(true);
                          setMessage("Request unarchived successfully.");
                          refetch?.();
                        },
                        onError: (err) => {
                          setLocalNotificationError(true);
                          setShowNotification(true);
                          setMessage(
                            err?.response?.data?.message ||
                              "Could not unarchive this request."
                          );
                        },
                      });
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm ${unarchivePending ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    <ArrowPathIcon className="w-[16px] h-[16px]" />
                    <span>Unarchive Request</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isLibraryBackendAdmin &&
            isDocLibrary &&
            isMyDocs &&
            libraryRequestId &&
            isNotArchivedRequest && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setArchiveModalTarget("library");
                      setArchiveModalOpen(true);
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <ArchiveBoxIcon className="w-[16px] h-[16px]" />
                    <span>Archive Request</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isRfiBackendAdmin &&
            isRfiType &&
            !isDocLibrary &&
            effectiveSponsorRfiResponseId &&
            !isLegacyRfi &&
            isRequestStatus("Archived") && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      unarchiveRfiMutate(effectiveSponsorRfiResponseId, {
                        onSuccess: () => {
                          setLocalNotificationError(false);
                          setShowNotification(true);
                          setMessage("Request unarchived successfully.");
                          refetch?.();
                        },
                        onError: (err) => {
                          setLocalNotificationError(true);
                          setShowNotification(true);
                          setMessage(
                            err?.response?.data?.message ||
                              "Could not unarchive this request."
                          );
                        },
                      });
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm ${unarchiveRfiPending ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    <ArrowPathIcon className="w-[16px] h-[16px]" />
                    <span>Unarchive Request</span>
                  </div>
                )}
              </MenuItem>
            )}
          {isDocLibraryCatalogAdmin && isRequestStatus("Archived") && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setDocumentArchiveModalMode("unarchive");
                    setDocumentArchiveModalOpen(true);
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm ${unarchiveDocumentPending ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <ArrowPathIcon className="w-[16px] h-[16px]" />
                  <span>Unarchive Document</span>
                </div>
              )}
            </MenuItem>
          )}
          {isDocLibraryCatalogAdmin && isNotArchivedRequest && (
            <MenuItem>
              {({ active }) => (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setDocumentArchiveModalMode("archive");
                    setDocumentArchiveModalOpen(true);
                  }}
                  className={`flex items-center space-x-2 ${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } block px-4 py-2 text-sm`}
                >
                  <ArchiveBoxIcon className="w-[16px] h-[16px]" />
                  <span>Archive Document</span>
                </div>
              )}
            </MenuItem>
          )}
          {isRfiBackendAdmin &&
            isRfiType &&
            !isDocLibrary &&
            effectiveSponsorRfiResponseId &&
            !isLegacyRfi &&
            isNotArchivedRequest && (
              <MenuItem>
                {({ active }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setArchiveModalTarget("rfi");
                      setArchiveModalOpen(true);
                    }}
                    className={`flex items-center space-x-2 ${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm`}
                  >
                    <ArchiveBoxIcon className="w-[16px] h-[16px]" />
                    <span>Archive Request</span>
                  </div>
                )}
              </MenuItem>
            )}
        </div>
      </MenuItems>

      <ArchiveLibraryRequestModal
        open={archiveModalOpen}
        onClose={() => {
          setArchiveModalOpen(false);
          setArchiveModalTarget(null);
        }}
        isSubmitting={archivePending || archiveRfiPending}
        requestLabel={
          archiveModalTarget === "rfi"
            ? details?.vendorName || details?.provider_name || ""
            : details?.provider_name || ""
        }
        onConfirm={(archiveComment) => {
          if (archiveModalTarget === "rfi" && effectiveSponsorRfiResponseId) {
            archiveRfiMutate(
              { sponsorRfiResponseId: effectiveSponsorRfiResponseId, archiveComment },
              {
                onSuccess: () => {
                  setArchiveModalOpen(false);
                  setArchiveModalTarget(null);
                  setShowNotification(true);
                  setMessage("Request archived successfully.");
                  refetch?.();
                },
                onError: (err) => {
                  setArchiveModalOpen(false);
                  setArchiveModalTarget(null);
                  setShowNotification(true);
                  setMessage(
                    err?.response?.data?.message ||
                      "Could not archive this request."
                  );
                },
              }
            );
            return;
          }
          archiveMutate(
            { requestId: libraryRequestId, archiveComment },
            {
              onSuccess: () => {
                setArchiveModalOpen(false);
                setArchiveModalTarget(null);
                setShowNotification(true);
                setMessage("Request archived successfully.");
                refetch?.();
              },
              onError: (err) => {
                setArchiveModalOpen(false);
                setArchiveModalTarget(null);
                setShowNotification(true);
                setMessage(
                  err?.response?.data?.message ||
                    "Could not archive this request."
                );
              },
            }
          );
        }}
      />

      <ArchiveDocumentModal
        open={documentArchiveModalOpen}
        mode={documentArchiveModalMode}
        onClose={() => setDocumentArchiveModalOpen(false)}
        isSubmitting={archiveDocumentPending || unarchiveDocumentPending}
        documentLabel={details?.document_name || details?.provider_name || ""}
        context={archiveDocumentContext}
        onConfirm={(archiveComment) => {
          const mutateFn =
            documentArchiveModalMode === "unarchive"
              ? unarchiveDocumentMutate
              : archiveDocumentMutate;
          mutateFn(
            { documentId: docId, archiveComment },
            {
              onSuccess: () => {
                setDocumentArchiveModalOpen(false);
                setLocalNotificationError(false);
                setShowNotification(true);
                setMessage(
                  documentArchiveModalMode === "unarchive"
                    ? "Document unarchived successfully."
                    : "Document archived successfully."
                );
                libraryRefetch?.();
                refetch?.();
              },
              onError: (err) => {
                setDocumentArchiveModalOpen(false);
                setLocalNotificationError(true);
                setShowNotification(true);
                setMessage(
                  err?.response?.data?.message ||
                    (documentArchiveModalMode === "unarchive"
                      ? "Could not unarchive this document."
                      : "Could not archive this document.")
                );
              },
            }
          );
        }}
      />

      <ReopenVqqConfirmModalSlot flow={reopenFlow} vendorName={resolveReopenVendorName(details)} />

      <Notification
        setShow={setShowNotification}
        show={showNotification && !isDownloading}
        status={localNotificationError ? "error" : "success"}
        errorMessage={message}
      />

      {/* Custom notification for download process that doesn't auto-hide */}
      {isDownloading && (
        <div
          aria-live="assertive"
          className="pointer-events-none mt-12 fixed inset-0 flex items-start px-4 py-6 sm:items-start sm:p-6 z-[9999]"
        >
          <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
            <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
              <div className="p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                  <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p className="text-blue-600" style={{ marginTop: "-3px" }}>
                      Downloading...
                    </p>
                    <p className="mt-1 text-base text-gray-500 break-words">
                      {message}
                    </p>
                  </div>
                  <div className="ml-4 flex flex-shrink-0">
                    <button
                      type="button"
                      className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => {
                        setIsDownloading(false);
                        setShowNotification(false);
                      }}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Menu>
  );
};
